import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Users,
  BookOpen,
  Calendar,
  Bell,
  Plug,
  Briefcase,
  Bot,
  Brain,
  Activity,
  Layers,
  Settings as SettingsIcon,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { useBranding } from "@/lib/use-branding";
import { api } from "@/lib/api-client";
import { catalogLabelForSlug } from "@/lib/catalog-labels";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

// Built dynamically inside the component so the "Properties" entry can
// rename per active vertical template (Restaurants / Treatments / Services
// / Menu & Catering / Properties).
function buildOperations(catalogLabel: string, CatalogIcon: LucideIcon): NavItem[] {
  return [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/inbox", label: "Inbox", icon: Inbox },
    { to: "/contacts", label: "Contacts", icon: Users },
    { to: "/properties", label: catalogLabel, icon: CatalogIcon },
    { to: "/visits", label: "Visits", icon: Calendar },
    { to: "/notifications", label: "Notifications", icon: Bell },
    { to: "/activity", label: "Activity log", icon: Activity },
  ];
}

const CONFIGURATION: NavItem[] = [
  { to: "/templates", label: "Templates", icon: Layers },
  { to: "/business-brain", label: "Business Brain", icon: Brain },
  { to: "/business", label: "Business", icon: Briefcase },
  { to: "/ai-bot", label: "AI Bot", icon: Bot },
  { to: "/knowledge", label: "Knowledge", icon: BookOpen },
  { to: "/connections", label: "Connections", icon: Plug },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const COLLAPSED_KEY = "ai-leasing-os.sidebar.collapsed";

interface SidebarProps {
  /** Open state for the mobile drawer (<md). Ignored on md+. */
  mobileOpen?: boolean;
  /** Called when the mobile drawer should close (backdrop click / nav). */
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const { name, logoUrl } = useBranding();

  // Read the active template so the "Properties" sidebar entry renames per
  // vertical (e.g. Restaurants / Treatments / Services / Menu & Catering).
  const { data: business } = useQuery({
    queryKey: ["settings", "business"],
    queryFn: () =>
      api.get<{ current_template_slug: string | null }>(
        "/api/settings/business",
      ),
    staleTime: 30_000,
  });
  const catalog = catalogLabelForSlug(business?.current_template_slug);
  const OPERATIONS = buildOperations(catalog.plural, catalog.icon);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes.
  const location = useLocation();
  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ESC closes the mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onMobileClose) onMobileClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileClose]);

  // Body content shared between desktop + mobile drawer.
  const inner = (
    <>
      {/* Header */}
      <div
        className={cn(
          "sidebar-header-gradient flex items-center gap-2 py-4",
          collapsed ? "px-2 justify-center" : "px-4",
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={name}
            className="h-8 w-8 rounded object-contain shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-white text-sm font-semibold"
            style={{ background: "var(--brand-primary)" }}
            aria-hidden
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold tracking-tight text-brand-primary">
              {name}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              dashboard
            </div>
          </div>
        )}
        {/* Mobile-only close button - replaces the collapse toggle */}
        {mobileOpen && onMobileClose && (
          <button
            type="button"
            onClick={onMobileClose}
            className="md:hidden text-slate-400 hover:text-slate-700"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-4 px-2 py-2 overflow-y-auto">
        <NavSection title="Operations" items={OPERATIONS} collapsed={collapsed} />
        <NavSection title="Configuration" items={CONFIGURATION} collapsed={collapsed} />
      </nav>

      {/* Collapse toggle - desktop only */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          "hidden md:flex items-center gap-2 px-3 py-2.5 border-t border-slate-200 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors",
          collapsed && "justify-center",
        )}
      >
        {collapsed ? (
          <PanelLeftOpen className="h-4 w-4" />
        ) : (
          <>
            <PanelLeftClose className="h-4 w-4" />
            Collapse
          </>
        )}
      </button>
    </>
  );

  return (
    <>
      {/* Desktop sidebar - always rendered at md+ */}
      <aside
        className={cn(
          "hidden md:flex h-full shrink-0 flex-col border-r border-slate-200 bg-brand-surface transition-[width] duration-200",
          collapsed ? "w-14" : "w-56",
        )}
      >
        {inner}
      </aside>

      {/* Mobile drawer - backdrop + slide-in panel */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-slate-900/40"
            onClick={onMobileClose}
            aria-hidden
          />
          <aside
            className={cn(
              "md:hidden fixed top-0 left-0 z-50 h-full w-64 flex flex-col",
              "border-r border-slate-200 bg-brand-surface shadow-xl",
              "animate-in slide-in-from-left",
            )}
            style={{ animation: "slide-in 200ms ease-out" }}
          >
            {inner}
          </aside>
          <style>{`
            @keyframes slide-in {
              from { transform: translateX(-100%); }
              to   { transform: translateX(0); }
            }
          `}</style>
        </>
      )}
    </>
  );
}

function NavSection({
  title, items, collapsed,
}: {
  title: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  return (
    <div>
      {!collapsed && (
        <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {title}
        </div>
      )}
      <div className="space-y-0.5">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                collapsed && "justify-center px-2",
                isActive
                  ? "nav-active-brand"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
              )
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
